"use client";

import SkeletonLoader from "@/components/layout/skeleton-loader/skeleton-loader";
import React, { useEffect, useState, useMemo } from "react";
import styles from "./card.module.scss";
import { generateImageFallbacks, useImageWithFallbacks } from "@/utils/nft-image-fallback.util";
import { getMultiTokenBackgroundColor } from "@/utils/token-color.util";

type Props = {
  src: string | undefined;
  alt: string;
  identifier: string;
  symbol?: string;
  contractAddress?: string;
  chainId?: number;
  tokenType?: string;
};

const CardImageLoader = ({ src, alt, identifier, symbol, contractAddress, chainId, tokenType = 'erc721' }: Props) => {
  const [fallbackUrls, setFallbackUrls] = useState<string[]>([]);
  const [isGeneratingFallbacks, setIsGeneratingFallbacks] = useState(true);

  // Generate fallback URLs for IPFS and other sources
  useEffect(() => {
    const generateFallbacks = async () => {
      setIsGeneratingFallbacks(true);
      const urls = await generateImageFallbacks(src, symbol, identifier, contractAddress, chainId, tokenType);
      setFallbackUrls(urls);
      setIsGeneratingFallbacks(false);
    };

    generateFallbacks();
  }, [src, symbol, identifier, contractAddress, chainId, tokenType]);

  const { currentUrl, isError, isLoading, handleError, handleLoad } = useImageWithFallbacks(fallbackUrls);

  // Get multi-token background color including identifier for uniqueness
  const colorConfig = useMemo(() =>
    getMultiTokenBackgroundColor(contractAddress, identifier, symbol),
    [contractAddress, identifier, symbol]
  );

  // Show loading state while generating fallbacks
  if (isGeneratingFallbacks) {
    return <SkeletonLoader />;
  }

  // If no URLs available or all failed, show placeholder with deterministic background or gravatar
  if (!currentUrl || isError) {
        return (
      <div
        className={styles.imageLoader}
        style={{
          backgroundColor: colorConfig.backgroundColor,
          position: 'relative' // Enable absolute positioning for text overlay
        }}
      >
        {colorConfig.gravatarUrl ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={colorConfig.gravatarUrl}
              alt={`Token ${identifier} pattern`}
              style={{
                borderRadius: '3px',
                objectFit: 'cover',
                zIndex: 1,
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0
              }}
              loading="lazy"
              draggable={false}
            />
            {/* Overlay text on top of gravatar pattern */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              textShadow: '0 0 4px rgba(0,0,0,0.8)', // Better readability over patterns
              zIndex: 2 // Ensure text appears on top of gravatar
            }}>
              {symbol && <div className={styles.tokenSymbol}>{symbol}</div>}
              <div className={styles.tokenIdentifier}>{identifier}</div>
            </div>
          </>
        ) : (
          <>
            {symbol && <div className={styles.tokenSymbol}>{symbol}</div>}
            <div className={styles.tokenIdentifier}>{identifier}</div>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={currentUrl}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        width={100}
        height={200}
        style={{
          objectFit: 'contain',
          borderRadius: '3px'
        }}
        loading="lazy"
        draggable={false}
      />
      {isLoading && <SkeletonLoader />}
    </>
  );
};

export default CardImageLoader;
